/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Dynamic Theme Provider & Token Consumer (Task 10.1.1)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { createContext, useContext, useEffect, useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { PublicThemeResponse, ThemeColorTokens } from '@sofia/shared-types';
import { client } from '../lib/api-client';

export type ThemeMode = 'light' | 'dark' | 'system';

interface ThemeContextType {
  themeMode: ThemeMode;
  setThemeMode: (mode: ThemeMode) => void;
  isDark: boolean;
  themeData: PublicThemeResponse | null;
  isLoading: boolean;
  refetchTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const THEME_MODE_STORAGE_KEY = 'sophia_theme_mode';
const THEME_TOKENS_STORAGE_KEY = 'sophia_cached_theme_tokens';

// Default Fallback Color Tokens (Electric Purple / Neon Cyan)
const DEFAULT_FALLBACK_TOKENS: { light: ThemeColorTokens; dark: ThemeColorTokens } = {
  light: {
    primary: '262 83% 58%',
    primaryHover: '262 83% 52%',
    primaryFg: '0 0% 100%',
    primary50: '262 100% 97%',
    primary100: '262 93% 94%',
    accent: '199 89% 48%',
    accentHover: '199 89% 42%',
    accentFg: '0 0% 100%',
    surface: '0 0% 100%',
    surfaceElevated: '0 0% 98%',
    border: '220 13% 91%',
    bgBase: '210 40% 98%',
    textMain: '222 47% 11%',
    textMuted: '215 16% 47%',
    muted: '210 40% 96%',
  },
  dark: {
    primary: '262 83% 58%',
    primaryHover: '262 83% 64%',
    primaryFg: '0 0% 100%',
    primary50: '262 50% 12%',
    primary100: '262 50% 18%',
    accent: '199 89% 48%',
    accentHover: '199 89% 54%',
    accentFg: '0 0% 100%',
    surface: '224 71% 4%',
    surfaceElevated: '224 71% 7%',
    border: '217 33% 17%',
    bgBase: '222 47% 11%',
    textMain: '210 40% 98%',
    textMuted: '215 20% 65%',
    muted: '217 33% 17%',
  },
};

/**
 * Apply CSS Custom Properties directly to document element.
 */
function applyCssTokens(tokens: ThemeColorTokens): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;

  root.style.setProperty('--color-primary', tokens.primary);
  root.style.setProperty('--color-primary-hover', tokens.primaryHover);
  root.style.setProperty('--color-primary-fg', tokens.primaryFg);
  if (tokens.primary50) root.style.setProperty('--color-primary-50', tokens.primary50);
  if (tokens.primary100) root.style.setProperty('--color-primary-100', tokens.primary100);

  root.style.setProperty('--color-accent', tokens.accent);
  root.style.setProperty('--color-accent-hover', tokens.accentHover);
  root.style.setProperty('--color-accent-fg', tokens.accentFg);

  root.style.setProperty('--color-surface', tokens.surface);
  root.style.setProperty('--color-surface-elevated', tokens.surfaceElevated);
  root.style.setProperty('--color-border', tokens.border);
  root.style.setProperty('--color-bg-base', tokens.bgBase);
  root.style.setProperty('--color-text-main', tokens.textMain);
  root.style.setProperty('--color-text-muted', tokens.textMuted);
  root.style.setProperty('--color-muted', tokens.muted);
}

export interface ThemeProviderProps {
  children: React.ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  // 1. Initialize user mode preference from localStorage
  const [themeMode, setThemeModeState] = useState<ThemeMode>(() => {
    if (typeof window === 'undefined') return 'system';
    return (localStorage.getItem(THEME_MODE_STORAGE_KEY) as ThemeMode) || 'system';
  });

  // 2. Track system dark mode preference
  const [systemIsDark, setSystemIsDark] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => setSystemIsDark(e.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // 3. Computed effective dark mode boolean
  const isDark = useMemo(() => {
    if (themeMode === 'dark') return true;
    if (themeMode === 'light') return false;
    return systemIsDark;
  }, [themeMode, systemIsDark]);

  // 4. Query dynamic theme tokens from Edge API with 24-hour cache
  const { data: themeData, isLoading, refetch } = useQuery<PublicThemeResponse | null>({
    queryKey: ['public-theme'],
    queryFn: async () => {
      try {
        const res = await client.api.v1.public.theme.$get();
        if (res.ok) {
          const json = await res.json();
          if ('data' in json && json.data) {
            // Cache tokens to localStorage for Zero-FOUT on next page load
            if (typeof window !== 'undefined') {
              localStorage.setItem(THEME_TOKENS_STORAGE_KEY, JSON.stringify(json.data.tokens));
            }
            return json.data as PublicThemeResponse;
          }
        }
      } catch (err) {
        console.warn('[ThemeProvider] Edge theme fetch failed, using fallback:', err);
      }
      return null;
    },
    staleTime: 24 * 60 * 60 * 1000, // 24 hours
    gcTime: 24 * 60 * 60 * 1000,
  });

  // 5. Update HTML class (.dark) and apply CSS custom properties
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;

    if (isDark) {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }

    const tokenSet = isDark
      ? themeData?.tokens?.dark ?? DEFAULT_FALLBACK_TOKENS.dark
      : themeData?.tokens?.light ?? DEFAULT_FALLBACK_TOKENS.light;

    applyCssTokens(tokenSet);

    // Synchronize dynamic favicon if configured
    if (themeData?.branding?.faviconUrl) {
      let link = document.querySelector("link[rel*='icon']") as HTMLLinkElement | null;
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }
      link.href = themeData.branding.faviconUrl;
    }
  }, [isDark, themeData]);

  // 6. User theme mode setter
  const setThemeMode = (mode: ThemeMode) => {
    setThemeModeState(mode);
    if (typeof window !== 'undefined') {
      localStorage.setItem(THEME_MODE_STORAGE_KEY, mode);
    }
  };

  const contextValue = useMemo(
    () => ({
      themeMode,
      setThemeMode,
      isDark,
      themeData: themeData ?? null,
      isLoading,
      refetchTheme: () => {
        void refetch();
      },
    }),
    [themeMode, isDark, themeData, isLoading, refetch],
  );

  return <ThemeContext.Provider value={contextValue}>{children}</ThemeContext.Provider>;
};

/**
 * Hook to access current theme state, mode changer, and active branding assets.
 */
export function useTheme(): ThemeContextType {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
